"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Calendar, Clock, MapPin, TagIcon } from "lucide-react";
import { useSession } from "next-auth/react";
import { motion, Variants } from "framer-motion";
import { IPackageRecord } from "../interface/IPackageData.interface";
import { months } from "@/data/tourPackagesData";
import { useModal } from "@/core/context/ModalContext";
import { showErrorMessage } from "@/utils/toastMessage/toast.message";

interface IProps {
  activeTab: string;
  content: IPackageRecord[];
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const GridView: React.FC<IProps> = ({ content }) => {
  const { status } = useSession();
  const { openModal } = useModal();
  const isLoggedIn = status === "authenticated";

  const handleNotLoggedIn = () => {
    openModal("login");
    showErrorMessage("Please login to access this feature.");
  };

  return (
    <motion.div
      className="w-full my-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {content.map((packageItem) => (
          <motion.div
            key={packageItem.id}
            variants={cardVariants}
            className="group rounded-xl border border-gray-200 p-4 transition-all duration-500 ease-in-out cursor-pointer bg-white hover:shadow-xl hover:border-primary-100"
          >
            <div className="relative h-72 w-full overflow-hidden rounded-xl mb-6">
              <Link href={`/package/${packageItem?.slug}`}>
                <Image
                  src={packageItem?.image || "/placeholder.png"}
                  alt={packageItem?.name || "Tour Package"}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </Link>
              <div className="absolute top-4 right-4 bg-white/70 backdrop-blur-md px-4 py-2 rounded-2xl shadow-sm z-10">
                <p className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                  <Clock size={14} className="text-gray-900" />
                  {packageItem?.duration} Days
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/60 backdrop-blur-md text-white px-4 py-2 rounded-2xl text-xs font-bold tracking-wide z-10">
                  <MapPin size={14} className="text-white" />
                  {packageItem?.destination}
                </div>
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/70 backdrop-blur-md text-black px-4 py-2 rounded-2xl text-xs font-bold tracking-wide z-10">
                  <TagIcon size={14} className="text-gray-900" />
                  {packageItem?.package_type?.name}
                </div>
              </div>
            </div>

            <div className="px-2">
              <Link
                href={`/package/${packageItem?.slug}`}
                className="flex justify-between items-start gap-4"
              >
                <h3 className="text-2xl font-bold text-gray-900 leading-tight transition-colors line-clamp-2 group-hover:text-primary-600">
                  {packageItem?.name}
                </h3>
              </Link>

              <div className="pt-6 flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                    Price starting at
                  </p>
                  <p className="text-2xl font-black text-gray-900">
                    NRS {packageItem?.current_price.toLocaleString()}
                  </p>
                </div>

                {isLoggedIn ? (
                  <Link href={`/user/book/${packageItem.id}`}>
                    <button className="bg-tertiary-400 text-white px-8 py-3.5 rounded-xl font-bold text-sm hover:bg-tertiary-500 transition-all active:scale-95 shadow-lg shadow-tertiary-400/20">
                      Book Now
                    </button>
                  </Link>
                ) : (
                  <button
                    onClick={handleNotLoggedIn}
                    className="bg-tertiary-400 text-white px-8 py-3.5 rounded-xl font-bold text-sm hover:bg-tertiary-500 transition-all cursor-pointer active:scale-95 shadow-lg shadow-tertiary-400/20"
                  >
                    Book Now
                  </button>
                )}
              </div>

              <div className="mt-6 flex items-center gap-3 bg-primary-50/80 p-3 rounded-2xl">
                <Calendar size={14} className="text-primary-400" />
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {months.map((month) => {
                    const isAvailable = packageItem?.availability_month?.some(
                      (m: string) => {
                        const formatted = m.toLowerCase().trim();
                        return (
                          formatted === month.toLowerCase() ||
                          formatted.startsWith(month.slice(0, 3).toLowerCase())
                        );
                      }
                    );

                    return (
                      <span
                        key={month}
                        className={`text-[10px] uppercase font-bold tracking-widest ${
                          isAvailable
                            ? "text-primary-500"
                            : "text-primary-300 line-through decoration-primary-300"
                        }`}
                      >
                        {month.slice(0, 3)}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default GridView;
